/**
 * Configuration resolved at runtime from /config.js, so the same built image serves local, staging and
 * production. Vite environment variables remain the fallback for `pnpm dev`.
 */
export interface RuntimeConfig {
  apiUrl: string;
  oidcAuthority: string;
  oidcClientId: string;
  defaultLocale: string;
  /** Where this roastery's own customers open the B2B portal (US-10.5); the slug is appended. */
  portalUrl: string;
  /** Where a platform operator belongs (US-14.0): this application is not their screen. */
  adminUrl: string;
  /** Where signing out lands (US-23.6) — the product's own front page, not another login screen. */
  landingUrl: string;
  /**
   * What this installation calls itself (US-33.1): the title bar of the installed application, and
   * the name under its icon. A setting because one image serves every environment, and one of those
   * environments may one day be somebody else's brand.
   */
  productName: string;
  /**
   * How fast characters have to arrive to count as a barcode scanner rather than a person (US-33.2),
   * in milliseconds between two keys. Absent everywhere it matters: the shipped default is fifty,
   * which no typist reaches and every scanner beats. It exists because an automated browser types
   * slower than a scanner does, and the alternative — loosening the rule in the code — would ship a
   * threshold a fast typist could trip.
   */
  scanMaxIntervalMs?: number;

  /** GlitchTip DSN of this application (US-18.9); empty = error tracking off. */
  sentryDsn?: string;
  /** Which installation (local, prod) and which commit - tags on every error event. */
  environment?: string;
  release?: string;
}

declare global {
  interface Window {
    __ROASTERY_CONFIG__?: Partial<RuntimeConfig>;
  }
}

const fallback: RuntimeConfig = {
  apiUrl: import.meta.env.VITE_API_URL ?? "http://api.localhost",
  oidcAuthority: import.meta.env.VITE_OIDC_AUTHORITY ?? "http://auth.localhost/realms/roastery-manager",
  oidcClientId: import.meta.env.VITE_OIDC_CLIENT_ID ?? "roastery-app",
  defaultLocale: import.meta.env.VITE_DEFAULT_LOCALE ?? "pl",
  portalUrl: import.meta.env.VITE_PORTAL_URL ?? "http://portal.localhost",
  adminUrl: import.meta.env.VITE_ADMIN_URL ?? "http://admin.localhost",
  landingUrl: import.meta.env.VITE_LANDING_URL ?? "http://localhost",
  productName: import.meta.env.VITE_PRODUCT_NAME ?? "BeanFlow",
};

export const runtimeConfig: RuntimeConfig = {
  ...fallback,
  ...(window.__ROASTERY_CONFIG__ ?? {}),
};
